import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatQuantity } from '../../../../utils/formatters';
import { useFiltroSocieta } from '../../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../../context/PermessiContext';
import { API_DOCS } from './WizardDistinte.api';
import './WizardDistinte.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoDistinta = 'MERCE' | 'LAVORAZIONI';

interface OrdineGenerabile {
  ordineId: number;
  numero: string;
  stato: string;
  dataConferma: string | null;
  clienteRagioneSociale: string | null;
  commessa: { id: number; numero: string; descrizione: string | null } | null;
  distinteEsistenti: number;
  righeMerceResidue: number;
  qtaMerceResidua: number;
  righeLavorazioniResidue: number;
  qtaLavorazioniResidua: number;
}

interface RisultatoGenerazione {
  ordineId: number;
  ordineNumero?: string;
  tipo?: TipoDistinta;
  esito: 'CREATA' | 'SALTATA' | 'ERRORE';
  distintaId?: number;
  numero?: number;
  etichetta?: string;
  nRighe?: number;
  stato?: string;
  motivo?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function renderTipo(tipo: TipoDistinta | undefined): React.ReactNode {
  if (!tipo) return <span className="bop-text-muted">—</span>;
  return tipo === 'MERCE'
    ? <span className="bop-badge bop-badge-info">Merce</span>
    : <span className="bop-badge wdist-badge-lavorazioni">Lavorazioni</span>;
}

function renderEsito(esito: RisultatoGenerazione['esito']): React.ReactNode {
  if (esito === 'CREATA') return <span className="bop-badge bop-badge-success">Creata</span>;
  if (esito === 'SALTATA') return <span className="bop-badge bop-badge-warning">Saltata</span>;
  return <span className="bop-badge bop-badge-danger">Errore</span>;
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function WizardDistinte() {
  const navigate = useNavigate();
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { isSuperadmin } = usePermesso();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Ricerca testo client-side
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [ordini, setOrdini] = useState<OrdineGenerabile[]>([]);
  const [loading, setLoading] = useState(false);

  // Selezione: set di ordineId per tipo
  const [selMerce, setSelMerce] = useState<Set<number>>(new Set());
  const [selLav, setSelLav] = useState<Set<number>>(new Set());

  // Opzioni generazione
  const [confermaSubito, setConfermaSubito] = useState(true);
  const [generando, setGenerando] = useState(false);

  // Esiti (null = fase di selezione, valorizzato = pannello risultati)
  const [risultati, setRisultati] = useState<RisultatoGenerazione[] | null>(null);
  const [createCount, setCreateCount] = useState(0);

  // Debounce ricerca testo
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // ── Caricamento ordini generabili ───────────────────────────────────────────

  const caricaOrdini = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/distinte/ordini-generabili?societaId&commessaId -> {"success":true,"data":[{"ordineId":"number","numero":"string","stato":"string","dataConferma":"string","clienteRagioneSociale":"string","commessa":{"id":"number","numero":"string","descrizione":"string"},"distinteEsistenti":"number","righeMerceResidue":"number","qtaMerceResidua":"number","righeLavorazioniResidue":"number","qtaLavorazioniResidua":"number"}]}
      const r = await apiFetch<{ success: boolean; data: OrdineGenerabile[] }>(
        `/api/distinte/ordini-generabili${qs}`
      );
      setOrdini(r.data || []);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento degli ordini generabili'), 'error');
      setOrdini([]);
    } finally {
      setLoading(false);
    }
    // Reset selezioni ad ogni ricarica (i dati possono essere cambiati)
    setSelMerce(new Set());
    setSelLav(new Set());
  }, [pronta, societaIdEff]);

  useEffect(() => { caricaOrdini(); }, [caricaOrdini]);

  // ── Filtro testo client-side (numero ordine / commessa / cliente) ──────────

  const ordiniFiltrati = useMemo(() => {
    if (!q) return ordini;
    const s = q.toLowerCase();
    return ordini.filter(o =>
      [
        o.numero,
        o.commessa?.numero,
        o.commessa?.descrizione,
        o.clienteRagioneSociale,
      ].some(v => (v || '').toLowerCase().includes(s))
    );
  }, [ordini, q]);

  // ── Selezione ───────────────────────────────────────────────────────────────

  const toggleMerce = (ordineId: number) => {
    setSelMerce(prev => {
      const next = new Set(prev);
      if (next.has(ordineId)) next.delete(ordineId); else next.add(ordineId);
      return next;
    });
  };

  const toggleLav = (ordineId: number) => {
    setSelLav(prev => {
      const next = new Set(prev);
      if (next.has(ordineId)) next.delete(ordineId); else next.add(ordineId);
      return next;
    });
  };

  // Seleziona tutte le righe merce/lavorazioni tra gli ordini visibili (filtrati)
  const selezionaTuttoMerce = () => {
    setSelMerce(prev => {
      const next = new Set(prev);
      for (const o of ordiniFiltrati) {
        if (o.righeMerceResidue > 0) next.add(o.ordineId);
      }
      return next;
    });
  };

  const selezionaTuttoLav = () => {
    setSelLav(prev => {
      const next = new Set(prev);
      for (const o of ordiniFiltrati) {
        if (o.righeLavorazioniResidue > 0) next.add(o.ordineId);
      }
      return next;
    });
  };

  const deselezionaTutto = () => {
    setSelMerce(new Set());
    setSelLav(new Set());
  };

  const nMerce = selMerce.size;
  const nLav = selLav.size;
  const nTotale = nMerce + nLav;

  // ── Generazione ─────────────────────────────────────────────────────────────

  const genera = async () => {
    if (nTotale === 0 || generando) return;

    // Costruisce gli elementi: un entry per ordine con i tipi selezionati
    const perOrdine = new Map<number, TipoDistinta[]>();
    selMerce.forEach(id => perOrdine.set(id, ['MERCE']));
    selLav.forEach(id => {
      const tipi = perOrdine.get(id);
      if (tipi) tipi.push('LAVORAZIONI'); else perOrdine.set(id, ['LAVORAZIONI']);
    });
    const elementi = Array.from(perOrdine.entries()).map(([ordineId, tipi]) => ({ ordineId, tipi }));

    setGenerando(true);
    try {
      // @api POST /api/distinte/genera-massive {"elementi":[{"ordineId":"number","tipi":["MERCE|LAVORAZIONI"]}],"conferma":"boolean"} -> {"success":true,"data":{"risultati":[{"ordineId":"number","ordineNumero":"string","tipo":"MERCE|LAVORAZIONI","esito":"CREATA|SALTATA|ERRORE","distintaId":"number","numero":"number","etichetta":"string","nRighe":"number","stato":"string","motivo":"string"}],"create":"number"}}
      const res = await apiFetch<{
        success: boolean;
        data?: { risultati: RisultatoGenerazione[]; create: number };
        error?: unknown;
      }>('/api/distinte/genera-massive', {
        method: 'POST',
        body: JSON.stringify({ elementi, conferma: confermaSubito }),
      });
      if (!res.success || !res.data) {
        showToast(estraiErrore({ payload: res }, 'Errore nella generazione delle distinte'), 'error');
        return;
      }
      const create = res.data.create || 0;
      showToast(`${create} distint${create === 1 ? 'a generata' : 'e generate'}`, 'success');
      setRisultati(res.data.risultati || []);
      setCreateCount(create);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nella generazione delle distinte'), 'error');
    } finally {
      setGenerando(false);
    }
  };

  // Torna alla fase di selezione e ricarica gli ordini residui
  const generaAltre = () => {
    setRisultati(null);
    setCreateCount(0);
    caricaOrdini();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="wdist">
        <BopBackLink to="/commerciale/attivo/distinte" label="Torna alle distinte" />

        {/* Header */}
        <div className="bop-page-header">
          <div className="wdist-header-titles">
            <h1 className="bop-page-title">Genera distinte</h1>
            <p className="bop-page-subtitle">
              Seleziona gli ordini appalto confermati e genera le distinte merce e
              lavorazioni in un colpo solo
            </p>
          </div>
        </div>

        {risultati !== null ? (
          /* ── Pannello esiti ─────────────────────────────────────────────── */
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">
                Esito generazione — {createCount} distint{createCount === 1 ? 'a creata' : 'e create'}
              </h2>
            </div>
            <div className="bop-card__body">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Ordine</th>
                    <th>Tipo</th>
                    <th>Esito</th>
                    <th>Distinta</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {risultati.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="bop-empty-state">Nessun esito da mostrare</div>
                      </td>
                    </tr>
                  ) : (
                    risultati.map((r, i) => (
                      <tr key={`${r.ordineId}-${r.tipo || 'x'}-${i}`}>
                        <td>{r.ordineNumero || `#${r.ordineId}`}</td>
                        <td>{renderTipo(r.tipo)}</td>
                        <td>{renderEsito(r.esito)}</td>
                        <td>
                          {r.distintaId && r.etichetta ? (
                            <Link
                              className="wdist-etichetta-link"
                              to={`/commerciale/attivo/distinte/${r.distintaId}`}
                            >
                              {r.etichetta}
                            </Link>
                          ) : (
                            <span className="bop-text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {r.motivo
                            ? <span className="bop-text-muted">{r.motivo}</span>
                            : <span className="bop-text-muted">—</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="wdist-esiti-actions">
                <button
                  className="bop-btn bop-btn-secondary"
                  onClick={generaAltre}
                >
                  Genera altre
                </button>
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={() => navigate('/commerciale/attivo/distinte')}
                >
                  Vai alle distinte
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* ── Fase di selezione ──────────────────────────────────────────── */
          <>
            {/* Filtri */}
            <div className="bop-filter-bar wdist-filters-row">
              {!isDisabled && (
                <div className="bop-form-group">
                  <label className="bop-label">Società</label>
                  <select
                    className="bop-select"
                    value={societaIdLocale}
                    onChange={e => setSocietaIdLocale(e.target.value)}
                  >
                    {isSuperadmin && <option value="">Tutte</option>}
                    {listaSocieta.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-label">Cerca</label>
                <input
                  className="bop-input wdist-search-input"
                  value={qInput}
                  onChange={e => setQInput(e.target.value)}
                  placeholder="Ordine, commessa, cliente..."
                />
              </div>
            </div>

            {/* Tabella selezione */}
            {!pronta ? (
              <div className="bop-card">
                <div className="bop-empty-state">
                  Seleziona una società per visualizzare gli ordini generabili
                </div>
              </div>
            ) : (
              <div className="bop-card">
                <div className="bop-card__header wdist-table-header">
                  <h2 className="bop-card__title">Ordini appalto confermati</h2>
                  <div className="wdist-toolbar">
                    <button
                      className="bop-btn bop-btn-secondary bop-btn-sm"
                      onClick={selezionaTuttoMerce}
                      disabled={loading}
                    >
                      Seleziona tutto merce
                    </button>
                    <button
                      className="bop-btn bop-btn-secondary bop-btn-sm"
                      onClick={selezionaTuttoLav}
                      disabled={loading}
                    >
                      Seleziona tutto lavorazioni
                    </button>
                    <button
                      className="bop-btn bop-btn-secondary bop-btn-sm"
                      onClick={deselezionaTutto}
                      disabled={loading || nTotale === 0}
                    >
                      Deseleziona tutto
                    </button>
                  </div>
                </div>
                <div className="bop-card__body">
                  <table className="bop-table">
                    <thead>
                      <tr>
                        <th className="wdist-col-check">Merce</th>
                        <th className="wdist-col-check">Lavorazioni</th>
                        <th>Ordine</th>
                        <th>Cliente</th>
                        <th>Commessa</th>
                        <th className="bop-text-right">Distinte esistenti</th>
                        <th className="bop-text-right">Righe merce residue</th>
                        <th className="bop-text-right">Righe lavorazioni residue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="bop-empty-state">Caricamento…</div>
                          </td>
                        </tr>
                      ) : ordiniFiltrati.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="bop-empty-state">
                              <h3 className="bop-empty-state__title">Nessun ordine generabile</h3>
                              <p className="bop-empty-state__text">
                                Non ci sono ordini appalto confermati con righe merce o
                                lavorazioni ancora da estrarre.
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        ordiniFiltrati.map(o => (
                          <tr key={o.ordineId}>
                            <td className="wdist-col-check">
                              <input
                                type="checkbox"
                                checked={selMerce.has(o.ordineId)}
                                disabled={o.righeMerceResidue <= 0}
                                title={o.righeMerceResidue <= 0 ? 'Niente da estrarre' : undefined}
                                onChange={() => toggleMerce(o.ordineId)}
                              />
                            </td>
                            <td className="wdist-col-check">
                              <input
                                type="checkbox"
                                checked={selLav.has(o.ordineId)}
                                disabled={o.righeLavorazioniResidue <= 0}
                                title={o.righeLavorazioniResidue <= 0 ? 'Niente da estrarre' : undefined}
                                onChange={() => toggleLav(o.ordineId)}
                              />
                            </td>
                            <td><span className="wdist-ordine-numero">{o.numero}</span></td>
                            <td>{o.clienteRagioneSociale || <span className="bop-text-muted">—</span>}</td>
                            <td>
                              {o.commessa ? (
                                <span>
                                  {o.commessa.numero}
                                  {o.commessa.descrizione ? ` — ${o.commessa.descrizione}` : ''}
                                </span>
                              ) : (
                                <span className="bop-text-muted">—</span>
                              )}
                            </td>
                            <td className="bop-text-right">{o.distinteEsistenti}</td>
                            <td className="bop-text-right">
                              {o.righeMerceResidue > 0 ? (
                                <span className="wdist-residuo-cell">
                                  {o.righeMerceResidue}
                                  <span className="bop-text-muted"> ({formatQuantity(o.qtaMerceResidua)})</span>
                                </span>
                              ) : (
                                <span className="bop-text-muted">—</span>
                              )}
                            </td>
                            <td className="bop-text-right">
                              {o.righeLavorazioniResidue > 0 ? (
                                <span className="wdist-residuo-cell">
                                  {o.righeLavorazioniResidue}
                                  <span className="bop-text-muted"> ({formatQuantity(o.qtaLavorazioniResidua)})</span>
                                </span>
                              ) : (
                                <span className="bop-text-muted">—</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Riepilogo sticky */}
            <div className="bop-card wdist-footer">
              <div className="bop-card__body">
                <div className="wdist-footer-row">
                  <div className="wdist-riepilogo">
                    <span className="wdist-riepilogo-testo">
                      Verranno generate <strong className="wdist-riepilogo-count">{nTotale}</strong>{' '}
                      distint{nTotale === 1 ? 'a' : 'e'} ({nMerce} merce, {nLav} lavorazioni)
                    </span>
                    <label className="wdist-conferma-check">
                      <input
                        type="checkbox"
                        checked={confermaSubito}
                        onChange={e => setConfermaSubito(e.target.checked)}
                      />
                      <span>
                        Conferma subito le distinte generate (alimentano subito i fabbisogni)
                      </span>
                    </label>
                  </div>
                  <div className="wdist-footer-actions">
                    <button
                      className="bop-btn bop-btn-secondary"
                      onClick={() => navigate('/commerciale/attivo/distinte')}
                      disabled={generando}
                    >
                      Annulla
                    </button>
                    <button
                      className="bop-btn bop-btn-primary"
                      onClick={genera}
                      disabled={generando || nTotale === 0}
                    >
                      {generando
                        ? 'Generazione…'
                        : `Genera ${nTotale} distint${nTotale === 1 ? 'a' : 'e'}`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
